import Vue from 'vue'
import Router from 'vue-router'
import Home from '@/views/Home'
import ReportList from '@/views/ReportList'
import Report from '@/views/Report'
import EmailModeration from '@/views/EmailModeration'
import MyReview from '@/views/MyReview'
import Unauthorized from '@/views/Unauthorized'
import FCSubscriptions from '@/views/FCSubscriptions'

Vue.use(Router)

import ARMLib from 'arm-vue-lib'

const FormSearch = ARMLib._components.GraphQLFormSearch
const FormEdit = ARMLib._components.GraphQLFormEdit

const redirections = [
  // People section (redirecting to generic forms now)
  {
    path: '/people',
    name: 'peopleSearch',
    redirect: {
      name: 'FormSearch',
      params: { formType: 'people' }
    }
  },
  {
    path: '/person/:personId/:section',
    name: 'personEditSection',
    redirect: to => ({
      name: 'FormEditRecordTab',
      params: {
        recordId: +to.params.personId,
        formType: 'people',
        activeTab: to.params.section
      }
    })
  },
  {
    path: '/person/:personId',
    name: 'personEdit',
    redirect: to => ({
      name: 'FormEditRecord',
      params: { recordId: +to.params.personId, formType: 'people' }
    })
  },
  // This will just go to user projects page, not pull up the project anymore
  {
    path: '/person/:personId/project/:projectTitle',
    name: 'personEditProject',
    redirect: to => ({
      name: 'FormEditRecordTab',
      params: {
        recordId: +to.params.personId,
        formType: 'people',
        activeTab: 'projects'
      }
    })
  },

  // Email Lists section (search, edit, and review redirect to generic forms now)
  {
    path: '/emails',
    name: 'emails',
    redirect: {
      name: 'home'
    }
  },
  {
    path: '/email/search',
    name: 'emailSearch',
    redirect: {
      name: 'FormSearch',
      params: { formType: 'emails' }
    }
  },
  {
    path: '/email/review',
    name: 'emailReview',
    redirect: {
      name: 'myReviewTab',
      params: { activeTab: 'active email lists (owner)' }
    }
  },
  {
    path: '/email/:emailList',
    name: 'emailListEdit',
    redirect: to => {
      if (to.params.emailList == 'moderation') {
        return { name: 'emailModeration' }
      }
      return {
        name: 'FormEditRecord',
        params: { recordId: to.params.emailList, formType: 'emails' }
      }
    },
    props: true
  },

  // Roles section (redirecting to generic forms now)
  {
    path: '/roles',
    name: 'roles',
    redirect: {
      name: 'FormSearch',
      params: { formType: 'roles' }
    }
  },
  {
    path: '/role/:roleName',
    name: 'roleEdit',
    redirect: to => ({
      name: 'FormEditRecord',
      params: { recordId: to.params.roleName, formType: 'roles' }
    })
  },

  // Groups section (redirecting to generic forms now)
  {
    path: '/groups',
    name: 'groups',
    redirect: {
      name: 'FormSearch',
      params: { formType: 'groups' }
    }
  },
  {
    path: '/group/:groupId',
    name: 'groupEdit',
    redirect: to => ({
      name: 'FormEditRecord',
      params: { recordId: to.params.groupId, formType: 'groups' }
    })
  },

  // Institutions section (redirecting to generic forms now)
  {
    path: '/institutions',
    name: 'institutions',
    redirect: {
      name: 'FormSearch',
      params: { formType: 'institutions' }
    }
  },
  {
    path: '/institutions/:instName',
    name: 'institutionEdit',
    redirect: to => ({
      name: 'FormEditRecord',
      params: { recordId: to.params.instName, formType: 'institutions' }
    })
  }
]

export default new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: redirections.concat([
    {
      path: '/',
      name: 'home',
      component: Home
    },

    // Generic forms
    {
      path: '/:formType/search',
      name: 'FormSearch',
      component: FormSearch,
      props(route) {
        const props = { ...route.params }
        return props
      }
    },
    {
      path: '/:formType/edit/:recordId',
      name: 'FormEditRecord',
      component: FormEdit,
      props(route) {
        const props = { ...route.params }
        if (props.recordId && !isNaN(props.recordId)) {
          props.recordId = +props.recordId
        }
        return props
      }
    },
    {
      path: '/:formType/edit/:recordId/:activeTab',
      name: 'FormEditRecordTab',
      component: FormEdit,
      props(route) {
        const props = { ...route.params }
        if (props.recordId && !isNaN(props.recordId)) {
          props.recordId = +props.recordId
        }
        return props
      }
    },
    {
      path: '/:formType/edit',
      name: 'FormEdit',
      component: FormEdit,
      props(route) {
        const props = { ...route.params }
        return props
      }
    },

    // Report section
    {
      path: '/reports',
      name: 'reportList',
      component: ReportList
    },
    {
      path: '/report/:report',
      name: 'runReport',
      component: Report,
      props: true
    },

    // Email moderation
    {
      path: '/email/moderation',
      name: 'emailModeration',
      component: EmailModeration
    },

    // My Reviews (person, groups (owner of), email lists (owner of), and subscriptions (email lists subscriber of))
    {
      path: '/review',
      name: 'myReview',
      component: MyReview,
      meta: {
        single: true,
        appTitle: 'My Reviews',
        appSubTitle: 'Request Person, Group, or Email List Changes',
        appShortTitle: 'MY REVIEW'
      }
    },
    {
      path: '/review/:activeTab',
      name: 'myReviewTab',
      component: MyReview,
      meta: {
        single: true,
        appTitle: 'My Reviews',
        appSubTitle: 'Request Person, Group, or Email List Changes',
        appShortTitle: 'MY REVIEW'
      },
      props: true
    },

    // Field Campaign Subscriptions Review
    {
      path: '/fc_subscriptions',
      name: 'fcSubscriptions',
      component: FCSubscriptions,
      meta: {
        single: true,
        appTitle: 'Field Campaign Subscriptions',
        appSubTitle: 'Add or remove yourself from field campaign email lists.',
        appShortTitle: 'FIELD CAMPAIGN SUBSCRIPTIONS'
      }
    },

    // Unauthorized page
    {
      path: '/unauthorized',
      name: 'Unauthorized',
      component: Unauthorized,
      meta: { single: true }
    },

    // Default route if none specified.
    { path: '*', redirect: '/' }
  ])
})
